import { useId, useState, useRef, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { FilterDef } from './chip-filters.types';
import styles from './chip-filters.module.scss';

interface Props {
  def: FilterDef;
  current: string | string[] | undefined;
  onApply: (next: string | string[] | undefined) => void;
  onClose: () => void;
}

function isSel(current: string | string[] | undefined, v: string): boolean {
  if (!current) return false;
  return Array.isArray(current) ? current.includes(v) : current === v;
}

export default function ValuePicker({ def, current, onApply, onClose }: Props) {
  const { t } = useTranslation();
  const multi = def.multi !== false;
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<string[]>(() => {
    if (!current) return [];
    return Array.isArray(current) ? [...current] : [current];
  });
  const listboxId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchPlaceholder =
    def.searchPlaceholder ?? t('chip-filters.searchIn', { label: def.label });

  const filtered = def.options.filter(
    (o) => !search || o.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (v: string) => {
    if (multi) {
      setDraft((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
    } else {
      onApply(isSel(current, v) ? undefined : v);
      onClose();
    }
  };

  const handleApply = () => {
    onApply(draft.length === 0 ? undefined : draft);
    onClose();
  };

  const handleClearDraft = () => {
    setDraft([]);
  };

  const handleSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      listRef.current?.querySelector<HTMLElement>('[data-picker-item]')?.focus();
    }
  };

  const handleItemKey = (e: KeyboardEvent<HTMLLIElement>, v: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle(v);
    }
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = e.currentTarget.nextElementSibling as HTMLElement | null;
      if (next) {
        next.focus();
      } else if (def.searchable) {
        searchRef.current?.focus();
      } else {
        listRef.current?.querySelector<HTMLElement>('[data-picker-item]')?.focus();
      }
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = e.currentTarget.previousElementSibling as HTMLElement | null;
      if (prev) {
        prev.focus();
      } else if (def.searchable) {
        searchRef.current?.focus();
      }
    }
  };

  return (
    <>
      {def.searchable && (
        <div className={styles.pickerSearch}>
          <input
            ref={searchRef}
            data-picker-search
            type="text"
            name={`${def.key}-filter-search`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            aria-controls={listboxId}
            autoComplete="off"
            autoFocus
            onKeyDown={handleSearchKey}
          />
        </div>
      )}

      {def.loading ? (
        <div className={styles.noOptions}>{t('general.loader')}</div>
      ) : filtered.length === 0 ? (
        <div className={styles.noOptions}>{t('chip-filters.noOptions')}</div>
      ) : (
        <ul
          id={listboxId}
          ref={listRef}
          className={styles.optionList}
          role="listbox"
          aria-label={def.label}
          aria-multiselectable={multi}
        >
          {filtered.map((opt) => {
            const selected = multi ? draft.includes(opt.value) : isSel(current, opt.value);
            return (
              <li
                key={opt.value}
                data-picker-item
                role="option"
                aria-selected={selected}
                tabIndex={0}
                className={clsx(styles.optionItem, selected && styles.optionSelected)}
                onClick={() => toggle(opt.value)}
                onKeyDown={(e) => handleItemKey(e, opt.value)}
              >
                {multi && (
                  <span
                    className={clsx(
                      styles.optionCheckbox,
                      selected && styles.optionCheckboxChecked
                    )}
                    aria-hidden="true"
                  />
                )}

                {opt.iconNode ? (
                  <span className={styles.optionIconWrap}>{opt.iconNode}</span>
                ) : opt.color ? (
                  <span className={styles.optionSwatch} style={{ background: opt.color }} />
                ) : null}

                <span className={styles.optionLabel}>{opt.label}</span>

                {!multi && selected && (
                  <span className={styles.optionCheckmark} aria-hidden="true">
                    <i className="ki-duotone ki-check-circle fs-6">
                      <span className="path1" />
                      <span className="path2" />
                    </i>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {multi && (
        <div className={styles.pickerFooter}>
          <button
            type="button"
            className="btn btn-sm btn-light-danger px-3 py-1 fw-semibold fs-8"
            onClick={handleClearDraft}
          >
            {t('chip-filters.clear')}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-light-primary px-3 py-1 fw-semibold fs-8"
            onClick={handleApply}
          >
            {t('chip-filters.apply')}
          </button>
        </div>
      )}
    </>
  );
}
